import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, HeadContent, Outlet, redirect, useMatches } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { sessionQueryOptions } from "../api/session";
import { AppShell } from "../components/shell/app-shell";
import { NotificationsMenu } from "../components/shell/notifications-menu";
import { Sidebar } from "../components/shell/sidebar";
import { SignOutButton } from "../components/shell/sign-out-button";
import { ThemeToggle } from "../components/shell/theme-toggle";
import { Toaster } from "../components/shell/toaster";
import { Topbar } from "../components/shell/topbar";
import { AppSplash } from "../components/status";

export interface RouterContext {
	readonly queryClient: QueryClient;
}

// Devtools are only bundled in development and skipped under Vitest.
const Devtools =
	import.meta.env.DEV && import.meta.env.MODE !== "test"
		? lazy(async () => {
				const [routerDevtools, queryDevtools] = await Promise.all([
					import("@tanstack/react-router-devtools"),
					import("@tanstack/react-query-devtools"),
				]);
				return {
					default: function DevtoolsPanel() {
						return (
							<>
								<routerDevtools.TanStackRouterDevtools position="bottom-right" />
								<queryDevtools.ReactQueryDevtools buttonPosition="bottom-left" />
							</>
						);
					},
				};
			})
		: () => null;

export const Route = createRootRouteWithContext<RouterContext>()({
	// Every page needs a session except routes marked `staticData: { public: true }`. The API keeps the session in
	// HttpOnly cookies and checks it on every request; the session query asks it once who is signed in
	// (src/api/session.ts), so this guard only shapes navigation.
	beforeLoad: async ({ context, location, matches }) => {
		if (matches.some(match => match.staticData.public === true)) {
			return;
		}
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null) {
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
	},
	head: () => ({ meta: [{ title: "Ravnar" }] }),
	// While the app starts there is no shell yet, so the whole screen shows the splash instead of a page skeleton.
	pendingComponent: AppSplash,
	component: RootLayout,
});

function RootLayout() {
	const isPublic = useMatches({ select: matches => matches.some(match => match.staticData.public === true) });

	return (
		<>
			<HeadContent />
			{isPublic ? (
				<Outlet />
			) : (
				<AppShell
					sidebar={<Sidebar />}
					topbar={
						<Topbar
							actions={
								<>
									<NotificationsMenu />
									<ThemeToggle />
									<SignOutButton />
								</>
							}
						/>
					}
				>
					<Outlet />
				</AppShell>
			)}
			{/* Outside the shell, so the public pages can show a toast too. */}
			<Toaster />
			<Suspense>
				<Devtools />
			</Suspense>
		</>
	);
}
